import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { useInventoryData } from 'hooks/useInventoryData';
import { useInventorySourceService } from 'services/inventorySource';
import { InventorySource } from 'types/inventory';
import { ROUTES } from 'utils/constants';
import Button from 'components/Button';
import Toast from 'components/Toast';
import RefreshButton from 'components/RefreshButton';
import Pagination from 'components/Pagination';
import { Search, PackageX, ArrowRight, Plus, AlertCircle } from 'lucide-react';
import styles from './InventoryList.module.scss';

// Format currency
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

function InventoryList() {
  const navigate = useNavigate();
  const inventorySourceService = useInventorySourceService();
  const [inventorySources, setInventorySources] = useState<InventorySource[]>(
    [],
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [showNoSourcesModal, setShowNoSourcesModal] = useState(false);

  // Use inventory data hook
  const { products, loading, reloading, error, reload } = useInventoryData();

  const showToast = useCallback(
    (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
    },
    [],
  );

  // Show error toast if there's an error
  useEffect(() => {
    if (error) {
      showToast(error, 'error');
    }
  }, [error, showToast]);

  const fetchInventorySources = useCallback(async () => {
    try {
      const sources = await inventorySourceService.getAllInventorySources();
      setInventorySources(sources);
    } catch {
      // Silently fail - we'll just check if sources array is empty
      setInventorySources([]);
    }
  }, [inventorySourceService]);

  useEffect(() => {
    void fetchInventorySources();
  }, [fetchInventorySources]);

  // Filter products based on search query
  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) {
      return products;
    }
    const query = searchQuery.toLowerCase();
    return products.filter(
      (product) =>
        product.name.toLowerCase().includes(query) ||
        (product.description?.toLowerCase().includes(query) ?? false) ||
        (product.inventoryCategory?.name.toLowerCase().includes(query) ??
          false) ||
        (product.salesCategory?.name.toLowerCase().includes(query) ?? false) ||
        (product.cogsCategory?.name.toLowerCase().includes(query) ?? false),
    );
  }, [products, searchQuery]);

  // Paginate filtered products
  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredProducts.slice(startIndex, endIndex);
  }, [filteredProducts, currentPage, pageSize]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const handleProductClick = useCallback(
    (productId: string) => {
      navigate(ROUTES.INVENTORY.PRODUCT.replace(':id', productId));
    },
    [navigate],
  );

  const handleAddInventoryClick = useCallback(() => {
    if (inventorySources.length === 0) {
      setShowNoSourcesModal(true);
    } else {
      navigate(ROUTES.INVENTORY.ADD);
    }
  }, [inventorySources.length, navigate]);

  return (
    <div className={styles.container}>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className={styles.header}>
        <h1 className={styles.title}>Inventory</h1>
        <div className={styles.headerActions}>
          <RefreshButton
            onClick={() => {
              reload();
              void fetchInventorySources();
            }}
            loading={reloading}
          />
          <Button
            onClick={handleAddInventoryClick}
            className={styles.addButton}
          >
            <Plus size={18} />
            Add Inventory
          </Button>
        </div>
      </div>

      {loading ? (
        <div className={styles.loading}>Loading inventory...</div>
      ) : (
        <div className={reloading ? styles.contentReloading : ''}>
          {error && !loading && (
            <div className={styles.errorState}>
              <p>{error}</p>
              <Button onClick={reload}>Retry</Button>
            </div>
          )}

          {!error && (
            <>
              <div className={styles.searchContainer}>
                <div className={styles.searchInputWrapper}>
                  <Search size={18} className={styles.searchIcon} />
                  <input
                    type="text"
                    placeholder="Search by product name, description, or category..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                    className={styles.searchInput}
                  />
                </div>
              </div>

              {filteredProducts.length === 0 ? (
                <div className={styles.emptyState}>
                  <PackageX size={48} className={styles.emptyIcon} />
                  <p>
                    {searchQuery
                      ? 'No inventory items found matching your search.'
                      : 'No inventory items yet. Products with inventory tracking enabled will appear here.'}
                  </p>
                </div>
              ) : (
                <>
                  <div className={styles.inventoryTable}>
                    <div className={styles.tableHeader}>
                      <div className={styles.tableHeaderCell}>Product Name</div>
                      <div className={styles.tableHeaderCell}>Description</div>
                      <div className={styles.tableHeaderCell}>Category</div>
                      <div className={styles.tableHeaderCell}>Price</div>
                      <div className={styles.tableHeaderCell}>Stock</div>
                      <div className={styles.tableHeaderCell} aria-hidden />
                    </div>
                    <div className={styles.tableBody}>
                      {paginatedProducts.map((product) => (
                        <div
                          key={product.id}
                          className={styles.tableRow}
                          onClick={() => handleProductClick(product.id)}
                        >
                          <div
                            className={styles.tableCell}
                            data-label="Product Name"
                          >
                            <div className={styles.productNameContainer}>
                              <span className={styles.productName}>
                                {product.name}
                              </span>
                            </div>
                          </div>
                          <div
                            className={styles.tableCell}
                            data-label="Description"
                          >
                            <span className={styles.productDescription}>
                              {product.description || '-'}
                            </span>
                          </div>
                          <div
                            className={styles.tableCell}
                            data-label="Category"
                          >
                            <span>{product.productCategory || '-'}</span>
                          </div>
                          <div className={styles.tableCell} data-label="Price">
                            <span className={styles.price}>
                              {formatCurrency(Number(product.price))}
                            </span>
                          </div>
                          <div className={styles.tableCell} data-label="Stock">
                            <span className={styles.stockQuantity}>
                              {product.inventory
                                ? Number(product.inventory.quantity)
                                : 0}{' '}
                              units
                            </span>
                          </div>
                          <div
                            className={styles.tableCell}
                            data-label="Actions"
                          >
                            <button
                              className={styles.viewButton}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleProductClick(product.id);
                              }}
                              aria-label={`View inventory for ${product.name}`}
                            >
                              <ArrowRight size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {Math.ceil(filteredProducts.length / pageSize) > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalItems={filteredProducts.length}
                      pageSize={pageSize}
                      onPageChange={handlePageChange}
                      onPageSizeChange={handlePageSizeChange}
                    />
                  )}
                </>
              )}
            </>
          )}

          {/* No Inventory Sources Modal */}
          <Modal
            show={showNoSourcesModal}
            onHide={() => setShowNoSourcesModal(false)}
            className={styles.modal}
            contentClassName={styles.modalContent}
            dialogClassName={styles.modalDialog}
            backdrop="static"
          >
            <Modal.Header closeButton className={styles.modalHeader}>
              <Modal.Title className={styles.modalTitle}>
                <AlertCircle size={20} style={{ marginRight: '0.5rem' }} />
                Inventory Sources Required
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className={styles.modalBody}>
              <div className={styles.noSourcesMessage}>
                <p>
                  Before you can add inventory, you need to set up at least one
                  inventory source.
                </p>
                <p>
                  Inventory sources represent where your inventory comes from
                  (e.g., Cash Purchase, Supplier Credit, Capital Contribution).
                  They are used to track the accounting category that will be
                  credited when adding inventory.
                </p>
                <p>
                  <strong>
                    Ask a company admin to create inventory sources, then try
                    adding inventory again.
                  </strong>
                </p>
              </div>
            </Modal.Body>
            <Modal.Footer className={styles.modalFooter}>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowNoSourcesModal(false)}
              >
                OK
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      )}
    </div>
  );
}

export default InventoryList;
